import type { WorkflowStatus } from '@/api/workflows';

/**
 * Whether the Archive action should be offered for a workflow card.
 *
 * Mirrors `WorkflowTemplate.Archive`'s guards exactly. It lives here, shared by
 * every tab that renders the button, because the previous arrangement — each
 * tab hand-rolling its own condition — is what put a permanently-failing button
 * on UAT twice over:
 *
 *   - The Approved tab tested liveness only, so a Draft or Pending card with a
 *     published baseline (`currentVersionNumber > 0`, `isActive === false`)
 *     showed a button the server always refused with a 400.
 *   - The Rejected tab tested nothing at all, so a live workflow whose new
 *     version had been rejected would have hit the "unpublish first" refusal.
 *
 * The two refusals below are about real-world hazard, not bookkeeping:
 *
 *   - **Pending** — archiving would strand it in the approval queue.
 *   - **Live published** — advisors are using it; unpublish it first.
 *
 * Everything else archives, Draft included. That is what makes archive →
 * restore → archive repeatable: restoring deliberately lands a workflow in
 * Draft, so refusing Draft here (and in the domain) made the archive page a
 * one-way door.
 */
export function canArchiveWorkflow(workflow: {
  status: WorkflowStatus;
  isActive: boolean;
  currentVersionNumber: number;
}): boolean {
  if (workflow.status === 'Pending') return false;
  if (workflow.isActive && workflow.currentVersionNumber > 0) return false;
  return true;
}
